import React from "react";
import { IoAppsOutline, IoLayersOutline, IoPersonOutline } from "react-icons/io5";
import {
  CONNECTOR_BRAND_ICON_BY_SOURCE,
  CONNECTOR_SOURCE_TYPES,
  resolveConnectorSourceType,
} from "./connectorBrandIcons";
import { isCaptureWorkflowItem, isManualActionItem } from "./actionItemHelpers";

const ICON_CLASS = "business-sidebar-nav-icon";

const iconClass = (modifier = "") =>
  modifier ? `${ICON_CLASS} ${ICON_CLASS}--${modifier}` : ICON_CLASS;

const WORKFLOW_TYPE_ICON_MODIFIER = {
  email_daily_digest: "gmail",
  zoom_daily_digest: "zoom",
  gong_daily_digest: "gong",
  granola_daily_digest: "granola",
  notion_daily_digest: "notion",
  google_calendar_daily_digest: "google-calendar",
  google_meet_daily_digest: "google-meet",
};

const KNOWN_SOURCE_TYPES = new Set(CONNECTOR_SOURCE_TYPES);

/** Substring hints used when a workflow has a name but no typed source key. */
const WORKFLOW_NAME_SOURCE_HINTS = [
  ["granola", "granola"],
  ["google meet", "google_meet"],
  ["google calendar", "google_calendar"],
  ["gmail", "gmail"],
  ["zoom", "zoom"],
  ["gong", "gong"],
  ["slack", "slack"],
  ["fireflies", "fireflies"],
  ["otter", "otter"],
  ["fathom", "fathom"],
  ["teams", "teams"],
  ["attio", "attio"],
  ["salesforce", "salesforce"],
  ["hubspot", "hubspot"],
  ["notion", "notion"],
  ["whatsapp", "whatsapp"],
];

const cssModifierForSourceType = (sourceType) =>
  String(sourceType || "")
    .trim()
    .replace(/_/g, "-");

/**
 * CSS modifier for a connector source key (`granola` → `granola`).
 *
 * @example
 * actionItemSourceToneModifier("google_calendar");
 * // "google-calendar"
 *
 * @example
 * actionItemSourceToneModifier("manual");
 * // ""
 */
export const actionItemSourceToneModifier = (sourceType) => {
  const key = String(sourceType || "").trim();
  if (!key || key === "manual") return "";
  return cssModifierForSourceType(key);
};

/**
 * Resolve the connector source key for an action item (granola, zoom, gmail, …).
 *
 * Manual / capture items return `"manual"`. Unknown connectors return `""`.
 *
 * @example
 * resolveActionItemConnectorSourceType(
 *   { workflow_id: "wf-1" },
 *   { workflowTypeById: new Map([["wf-1", "granola_daily_digest"]]) }
 * );
 * // "granola"
 *
 * @example
 * resolveActionItemConnectorSourceType({ file_id: "manual::abc" });
 * // "manual"
 */
export const resolveActionItemConnectorSourceType = (item, { workflowTypeById } = {}) => {
  if (!item || typeof item !== "object") return "";
  if (isManualActionItem(item) || isCaptureWorkflowItem(item)) return "manual";

  const workflowId = String(item.workflow_id || "").trim();
  const workflowType =
    String(item.workflow_type || "").trim() ||
    String(workflowTypeById?.get(workflowId) || "").trim();
  const fromType = resolveConnectorSourceType(workflowType);
  if (KNOWN_SOURCE_TYPES.has(fromType)) return fromType;

  const workflowName = String(item.workflow_name || "").trim().toLowerCase();
  if (!workflowName) return "";
  if (workflowName.includes("email") && !workflowName.includes("gmail")) return "gmail";
  const hinted = WORKFLOW_NAME_SOURCE_HINTS.find(([needle]) =>
    workflowName.includes(needle)
  );
  return hinted ? hinted[1] : "";
};

/**
 * Icon for a workflow type in the sources sidebar.
 *
 * @example
 * renderWorkflowSourceIcon("zoom_daily_digest");
 * // <ZoomBrandIcon className="business-sidebar-nav-icon business-sidebar-nav-icon--zoom" />
 */
export const renderWorkflowSourceIcon = (workflowType) => {
  const key = String(workflowType || "").trim();
  const sourceType = resolveConnectorSourceType(key);
  const Icon = CONNECTOR_BRAND_ICON_BY_SOURCE[sourceType];
  const modifier =
    WORKFLOW_TYPE_ICON_MODIFIER[key] || cssModifierForSourceType(sourceType);
  if (Icon) {
    return (
      <Icon
        className={iconClass(modifier)}
        aria-hidden="true"
      />
    );
  }
  return <IoAppsOutline className={iconClass("default")} aria-hidden="true" />;
};

/**
 * Icon for the “All tasks” sidebar row.
 *
 * @example
 * renderAllTasksSourceIcon();
 */
export const renderAllTasksSourceIcon = () => (
  <IoLayersOutline className={iconClass("all")} aria-hidden="true" />
);

/**
 * Icon for manual / “Added by you” tasks.
 *
 * @example
 * renderManualSourceIcon();
 */
export const renderManualSourceIcon = () => (
  <IoPersonOutline className={iconClass("manual")} aria-hidden="true" />
);

/**
 * Brand icon for the source row on a task details panel.
 *
 * @example
 * renderActionItemSourceIcon(
 *   { workflow_id: "wf-1" },
 *   { workflowTypeById: new Map([["wf-1", "granola_daily_digest"]]) }
 * );
 * // <GranolaBrandIcon className="business-sidebar-nav-icon business-sidebar-nav-icon--granola" />
 *
 * @example
 * renderActionItemSourceIcon({ file_id: "manual::abc" });
 * // person outline icon
 */
export const renderActionItemSourceIcon = (item, { workflowTypeById } = {}) => {
  const sourceType = resolveActionItemConnectorSourceType(item, { workflowTypeById });
  if (sourceType === "manual") return renderManualSourceIcon();
  if (sourceType) return renderWorkflowSourceIcon(sourceType);
  return <IoAppsOutline className={iconClass("default")} aria-hidden="true" />;
};
